import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import FrameworkUtils from 'utils/framework';
import PaymentMethodList from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/PaymentMethodList';
import isPayPalActivePaymentMethod from 'components/FrictionlessCheckout/PaymentReleaseFour/PaymentMethodList/isPayPalActivePaymentMethod';
import { orderDetailsSelector } from 'selectors/order/orderDetails/orderDetailsSelector';
import priceInfoSelector from 'selectors/order/orderDetails/priceInfo/priceInfoSelector';
import { orderSelector } from 'selectors/order/orderSelector';
import checkoutUtils from 'utils/Checkout';
import orderUtils from 'utils/Order';
import rwdBasketUtils from 'utils/RwdBasket';
import paymentOptionsSelector from 'selectors/order/paymentOptionsSelector';
import paypalSelector from 'selectors/paypal/paypalSelector';
import {
    commonOrderToggleActions,
    updatePaymentActions,
    sectionSaveOrderAction,
    swapPaypalToCreditCard,
    payPalWithInterstice
} from 'services/Checkout';
import decorators from 'utils/decorators';
import { INTERSTICE_DELAY_MS } from 'constants/Checkout';
import checkoutApi from 'services/api/checkout';
import profileApi from 'services/api/profile';
import processEvent from 'analytics/processEvent';
import anaConsts from 'analytics/constants';
import ErrorsUtils from 'utils/Errors';
import Location from 'utils/Location';
import store from 'store/Store';
import UtilActions from 'utils/redux/Actions';
import OrderActions from 'actions/OrderActions';
import KlarnaActions from 'actions/KlarnaActions';
import AfterpayActions from 'actions/AfterpayActions';
import PazeActions from 'actions/PazeActions';
import VenmoActions from 'actions/VenmoActions';
import { setReady as payPalSetReady } from 'actions/PayPalActions';
import GooglePayActions from 'actions/GooglePayActions';
import { refreshCheckout } from 'services/Checkout';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import { SECTION_NAMES } from 'constants/frictionlessCheckout';
import Actions from 'actions/Actions';
import agentAwareUtils from 'utils/AgentAware';
import PaymentEN from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_en_US';
import PaymentFR from 'components/FrictionlessCheckout/PaymentReleaseFour/locales/Payment_fr_CA';
import PaymentUtils from 'utils/payment';
import { isUserSDUTrialEligibleSelector } from 'viewModel/selectors/user/isUserSDUTrialEligibleSelector';
import { localeSelector } from 'viewModel/selectors/locale/localeSelector';
import paypalLegalNoticeSelector from 'components/FrictionlessCheckout/PaymentReleaseFour/utils/paypalLegalNoticeSelector';

const { getInstallment } = PaymentUtils;
const { wrapHOC } = FrameworkUtils;
const { getTextFromResource, getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(PaymentEN, PaymentFR);
const maxCreditCards = Sephora.configurationSettings.maxCreditCards;

const localization = createStructuredSelector({
    expires: getTextFromResource(getText, 'expires'),
    storeCreditApplied: getTextFromResource(getText, 'storeCreditApplied'),
    storeCreditNotEnoughMessage: getTextFromResource(getText, 'storeCreditNotEnoughMessage'),
    accountCreditAltText: getTextFromResource(getText, 'accountCreditAltText'),
    removeAddress: getTextFromResource(getText, 'removeAddress'),
    maxCreditCardsMessage: getTextFromResource(getText, 'maxCreditCardsMessage', [maxCreditCards]),
    continueButton: getTextFromResource(getText, 'continueButton'),
    paymentMethodSelection: getTextFromResource(getText, 'paymentMethodSelection'),
    afterpayHeader: getTextFromResource(getText, 'afterpayHeader'),
    afterpaySubtitle: getTextFromResource(getText, 'afterpaySubtitle', ['{0}']),
    afterpaySaveAndContinue: getTextFromResource(getText, 'afterpaySaveAndContinue'),
    afterpayCancel: getTextFromResource(getText, 'afterpayCancel'),
    afterpayContinueWithAfterpay: getTextFromResource(getText, 'afterpayContinueWithAfterpay'),
    afterpayLoading: getTextFromResource(getText, 'afterpayLoading'),
    afterpayErrorMessage: getTextFromResource(getText, 'afterpayErrorMessage'),
    afterpayApiErrorFallback: getTextFromResource(getText, 'afterpayApiErrorFallback'),
    afterpayGiftCardWarning: getTextFromResource(getText, 'afterpayGiftCardWarning'),
    afterpayLegalNotice: getTextFromResource(getText, 'afterpayLegalNotice'),
    addNewCard: getTextFromResource(getText, 'addNewCard'),
    useThisCard: getTextFromResource(getText, 'useThisCard'),
    maxGiftCards: getTextFromResource(getText, 'maxGiftCards'),
    giftCardAdditionalPaymentRequired: getTextFromResource(getText, 'giftCardAdditionalPaymentRequired'),
    klarnaHeader: getTextFromResource(getText, 'klarnaHeader'),
    klarnaSubtitle: getTextFromResource(getText, 'klarnaSubtitle', ['{0}']),
    klarnaSaveAndContinue: getTextFromResource(getText, 'klarnaSaveAndContinue'),
    klarnaCancel: getTextFromResource(getText, 'klarnaCancel'),
    klarnaLoading: getTextFromResource(getText, 'klarnaLoading'),
    klarnaApiErrorFallback: getTextFromResource(getText, 'klarnaApiErrorFallback'),
    klarnaGiftCardWarning: getTextFromResource(getText, 'klarnaGiftCardWarning'),
    klarnaFeatureBuyerProtection: getTextFromResource(getText, 'klarnaFeatureBuyerProtection'),
    klarnaTermsText: getTextFromResource(getText, 'klarnaTermsText'),
    // For fr_CA we read a hardcoded key that has no _en_US.js counterpart so the SDL translation bot cannot overwrite the legally-reviewed copy.
    klarnaLegalNotice: createSelector(localeSelector, () =>
        getText(LanguageLocaleUtils.isFRCanada() ? 'klarnaLegalNoticeHardcoded' : 'klarnaLegalNotice')
    ),
    klarnaBillingAddressCheckbox: getTextFromResource(getText, 'klarnaBillingAddressCheckbox'),
    pazeHeader: getTextFromResource(getText, 'pazeHeader'),
    pazeSubtitle: getTextFromResource(getText, 'pazeSubtitle'),
    pazeGiftCardWarning: getTextFromResource(getText, 'pazeGiftCardWarning'),
    pazeSaveAndContinue: getTextFromResource(getText, 'pazeSaveAndContinue'),
    pazeCancel: getTextFromResource(getText, 'pazeCancel'),
    paypalHeader: getTextFromResource(getText, 'paypalHeader'),
    paypalLegalNotice: paypalLegalNoticeSelector,
    paypalSaveAndContinue: getTextFromResource(getText, 'paypalSaveAndContinue'),
    paypalCancel: getTextFromResource(getText, 'paypalCancel'),
    nonCombinableGiftCardPartners: getTextFromResource(getText, 'nonCombinableGiftCardPartners'),
    useThisPaymentMethod: getTextFromResource(getText, 'useThisPaymentMethod'),
    giftCardsNotCombinable: getTextFromResource(getText, 'giftCardsNotCombinable'),
    giftCardsNotCombinableWithThisPayment: getTextFromResource(getText, 'giftCardsNotCombinableWithThisPayment'),
    cantUseForThisOrder: getTextFromResource(getText, 'cantUseForThisOrder'),
    seeDetails: getTextFromResource(getText, 'seeDetails'),
    alternatePaymentDetailsTitle: getTextFromResource(getText, 'alternatePaymentDetailsTitle'),
    alternatePaymentDetailsMessage: getTextFromResource(getText, 'alternatePaymentDetailsMessage'),
    gotIt: getTextFromResource(getText, 'gotIt'),
    and: getTextFromResource(getText, 'and'),
    accountCreditCoversFullOrder: getTextFromResource(getText, 'accountCreditCoversFullOrder'),
    accountCreditLessThanTotal: getTextFromResource(getText, 'accountCreditLessThanTotal'),
    accountCreditApplied: getTextFromResource(getText, 'accountCreditApplied'),
    subscriptionCreditCardRequired: getTextFromResource(getText, 'subscriptionCreditCardRequired'),
    accountCreditSubscriptionMessage: getTextFromResource(getText, 'accountCreditSubscriptionMessage'),
    accountCreditAndGCLessThanTotal: getTextFromResource(getText, 'accountCreditAndGCLessThanTotal'),
    maximumReached: getTextFromResource(getText, 'maximumReached'),
    maxCardsReachedMessage: getTextFromResource(getText, 'maxCardsReachedMessage'),
    ok: getTextFromResource(getText, 'ok'),
    removeCreditCard: getTextFromResource(getText, 'removeCreditCard'),
    areYouSureMessage: getTextFromResource(getText, 'areYouSureMessage'),
    remove: getTextFromResource(getText, 'remove'),
    cancel: getTextFromResource(getText, 'cancel'),
    deleteCard: getTextFromResource(getText, 'deleteCard'),
    expiredCreditCardMsg: getTextFromResource(getText, 'expiredCreditCardMsg'),
    edit: getTextFromResource(getText, 'edit')
});

const applePayFlowSelector = createSelector(orderSelector, order => order.isApplePayFlow);

const orderMergeAction = bankRewardsValidPaymentsMessage => {
    store.dispatch(UtilActions.merge('order', 'bankRewardsValidPaymentsMessage', bankRewardsValidPaymentsMessage));
};

const sectionSaveAction = (value, component, isUpdateOrder, isPaymentSectionComplete) => {
    store.dispatch(OrderActions.sectionSaved(value, component, isUpdateOrder, isPaymentSectionComplete));
};

const actionModal = (title, message, confirmButtonText) =>
    store.dispatch(
        Actions.showInfoModal({
            isOpen: true,
            title: title,
            message: message,
            confirmButtonText: confirmButtonText
        })
    );

const newCreditCardWithInterstice = (defaultPayment, creditCardOptions, showEditCreditCardForm) => {
    return (newCreditCard, label, component, saveSuccessCallback) =>
        decorators
            .withInterstice(checkoutApi.updateCreditCardOnOrder, INTERSTICE_DELAY_MS)(newCreditCard, label)
            .then(data => {
                if (data) {
                    orderMergeAction(data.bankRewardsValidPaymentsMessage);
                }

                swapPaypalToCreditCard();
                sectionSaveAction(Location.getLocation().pathname, component);
                refreshCheckout()().then(() => saveSuccessCallback());

                processEvent.process(anaConsts.ADD_PAYMENTINFO_EVENT, {
                    data: {
                        paymentType: defaultPayment
                    }
                });
            })
            .catch(errorData => {
                if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
                    //If it is Sephora mirror and the new credit card is expired then specific error is displayed for agents
                    const isCreditCardExpired = errorData.errorMessages.includes('Expired credit card.');

                    if (isCreditCardExpired) {
                        const selectedCreditCard = creditCardOptions.find(card => card.creditCardId === newCreditCard.creditCard.creditCardId);
                        showEditCreditCardForm({
                            ...selectedCreditCard,
                            isExpired: true,
                            expirationMonth: '',
                            expirationYear: ''
                        });
                    } else {
                        ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
                    }
                } else {
                    ErrorsUtils.collectAndValidateBackEndErrors(errorData, this);
                }
            });
};

const functions = {
    updatePaymentActions,
    commonOrderToggleActions,
    sectionSaveOrderAction,
    swapPaypalToCreditCard,
    utilMerge: UtilActions.merge,
    merge: UtilActions.merge,
    togglePlaceOrderDisabled: OrderActions.togglePlaceOrderDisabled,
    klarnaToggle: KlarnaActions.toggleSelect,
    klarnaSetReady: KlarnaActions.setReady,
    afterPayToggle: AfterpayActions.toggleSelect,
    KlarnaBackgroundInit: KlarnaActions.backgroundInit,
    afterpaySetReady: AfterpayActions.setReady,
    pazeToggle: PazeActions.toggleSelect,
    pazeSetReady: PazeActions.setReady,
    paypalSetReady: payPalSetReady,
    googlePaySetReady: GooglePayActions.setReady,
    loadPaze: PazeActions.loadIframe,
    venmoToggle: VenmoActions.toggleSelect,
    afterPayResetError: AfterpayActions.resetError,
    loadExpandedViewWidget: AfterpayActions.loadExpandedViewWidget,
    setPaymentMethodAfterPay: AfterpayActions.setPaymentMethod,
    setPaymentMethodNeedsConfirmation: OrderActions.setPaymentMethodNeedsConfirmation,
    setCheckoutSectionErrors: OrderActions.setCheckoutSectionErrors,
    savePendingCreditCard: OrderActions.savePendingCreditCard,
    clearPendingCreditCard: OrderActions.clearPendingCreditCard,
    clearNamedSectionErrors: OrderActions.clearNamedSectionErrors,
    showConfirmCVVModal: Actions.showConfirmCVVModal,
    showInfoModal: Actions.showInfoModal,
    orderMerge: (path, key, value) => store.dispatch(UtilActions.merge(path, key, value)),
    actionModal,
    updateOrder: OrderActions.updateOrder,
    showCVCInfoModal: OrderActions.showCVCInfoModal
};

const utilityFunctions = {
    removeCreditCardFromProfile: (profileId, creditCardId) => {
        return decorators.withInterstice(profileApi.removeCreditCardFromProfile, INTERSTICE_DELAY_MS)(profileId, creditCardId);
    },
    removeOrderPayment: (orderId, creditCardGroupId, creditCardId) => {
        return decorators.withInterstice(checkoutApi.removeOrderPayment, INTERSTICE_DELAY_MS)(orderId, creditCardGroupId, creditCardId);
    },
    getOrderDetails: orderId => {
        return decorators.withInterstice(checkoutApi.getOrderDetails, INTERSTICE_DELAY_MS)(orderId);
    },
    getCreditCards: (orderId, shipCountry, isFilterSelected) => {
        return decorators.withInterstice(checkoutApi.getCreditCards, INTERSTICE_DELAY_MS)(orderId, shipCountry, isFilterSelected);
    },
    setDefaultCreditCardOnProfile: nextDefaultCreditCardId => {
        return decorators.withInterstice(profileApi.setDefaultCreditCardOnProfile, INTERSTICE_DELAY_MS)(nextDefaultCreditCardId);
    }
};

const fields = createSelector(
    orderDetailsSelector,
    priceInfoSelector,
    applePayFlowSelector,
    paymentOptionsSelector,
    orderSelector,
    isUserSDUTrialEligibleSelector,
    paypalSelector,
    localization,
    (orderDetails, priceInfo, isApplePayFlow, paymentOptions, order, isUserSDUTrialEligible, paypal, locales) => {
        const isZeroCheckout = orderUtils.isZeroCheckout();
        const isPayPalPayLaterEligible = orderDetails?.items?.isPayPalPayLaterEligible;
        const isGuestOrder = checkoutUtils.isGuestOrder();
        // Fall back to the Redux `paypal.isSelected` flag because the order-derived check
        // (PayPal payment group + paypalAmount) lags behind the new Braintree "Save and Continue"
        // flow — it dispatches paypalToggleSelect(true) but doesn't add the PayPal payment group
        // to the order. Without this fallback, L2 reads isPayPalSelected=false on "Change",
        // setSelectedAlternatePaymentType resolves to the prior payment from the order, and the
        // wrong radio appears checked (PAY-7021). Mirrors PaymentReleaseFour/index.js (L1).
        const isPayPalSelected = isPayPalActivePaymentMethod(orderDetails, priceInfo) || Boolean(paypal?.isSelected);
        const hasSDUInBasket = orderUtils.hasSDUInBasket(orderDetails);
        const hasFreeSDUTrialOnlyInBasket = orderUtils.hasFreeSDUTrialOnlyInBasket(orderDetails, isUserSDUTrialEligible);
        const hasAutoReplenishItemInBasket = orderUtils.hasAutoReplenishItems(orderDetails);
        let shouldDisplayAddOrDeleteCreditCardButton = !isZeroCheckout || hasSDUInBasket || hasAutoReplenishItemInBasket;
        const giftCardPaymentGroups = orderUtils.getGiftCardPaymentGroups(orderDetails);
        const canRedeemGiftCards = giftCardPaymentGroups.length < 2;
        const orderHasGiftCardItem = orderDetails?.items?.items ? rwdBasketUtils.hasPhysicalGiftCardInBasket(orderDetails.items) : false;

        if (Sephora.isAgent && agentAwareUtils.isAgentAwareSession()) {
            //If it is Sephora Mirror, agent should see add card button only if they are tier 3
            shouldDisplayAddOrDeleteCreditCardButton = agentAwareUtils.isAgentAuthorized(['3']);
        }

        const isBopis = orderDetails?.header?.isBopisOrder;
        const creditCardPaymentGroup = order.pendingCreditCard || orderUtils.getCreditCardPaymentGroup(orderDetails) || { isComplete: false };
        const gcAmountNotEnoughMessage = checkoutUtils.getGiftCardAmountNotEnoughMessage(orderDetails?.paymentGroups?.paymentMessages);
        const isCreditCardRequiredMessage = checkoutUtils.getCreditCardRequiredMessage(orderDetails?.paymentGroups?.paymentMessages);
        const isPaymentNotComplete = orderDetails?.paymentGroups?.paymentGroupsEntries?.some(
            paymentGroup => paymentGroup?.paymentGroup.isComplete === false
        );
        const isPaymentNotSelectedError =
            isPaymentNotComplete &&
            FrictionlessUtils.validatePaymentGroup(
                orderDetails?.paymentGroups?.paymentGroupsEntries,
                orderDetails?.paymentGroups?.paymentMessages?.[0],
                orderDetails?.header?.isComplete
            );

        const sectionErrors = {};
        sectionErrors[SECTION_NAMES.PAYMENT] = isPaymentNotSelectedError;

        return {
            orderDetails,
            priceInfo,
            isApplePayFlow,
            isZeroCheckout,
            paymentOptions,
            isPayPalPayLaterEligible,
            isGuestOrder,
            isPayPalSelected,
            shouldDisplayAddOrDeleteCreditCardButton,
            hasAutoReplenishItemInBasket,
            isBopis,
            creditCardPaymentGroup,
            newCreditCardWithInterstice,
            gcAmountNotEnoughMessage,
            isCreditCardRequiredMessage,
            locales,
            sectionErrors,
            canRedeemGiftCards,
            orderHasGiftCardItem,
            installmentValue: getInstallment(orderDetails),
            hasPendingCreditCard: Boolean(order.pendingCreditCard),
            payPalWithInterstice,
            haslternatePaymentMethodSelected: FrictionlessUtils.getActiveAlternatePaymentFromOrder(orderDetails),
            confirmCVVReason: order.confirmCVVReason,
            pendingCreditCard: order.pendingCreditCard,
            giftCardPaymentGroups,
            hasFreeSDUTrialOnlyInBasket
        };
    }
);

const withPaymentMethodListToProps = wrapHOC(
    connect(fields, functions, (stateProps, dispatchProps, ownProps) => ({
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        ...utilityFunctions
    }))
);

export default withPaymentMethodListToProps(PaymentMethodList);
